/**
 * Utility functions for handling and formatting time in 12-hour Eastern Standard Time (EST/EDT).
 */

/**
 * Formats a time string (24-hour "15:00", 12-hour "3:00 PM", or ISO timestamp) into 12-hour Eastern Standard Time format.
 * Examples:
 * - "15:00" -> "3:00 PM EST"
 * - "09:30" -> "9:30 AM EST"
 * - "12:00" -> "12:00 PM EST"
 * - "00:15" -> "12:15 AM EST"
 * - "3:00 PM" -> "3:00 PM EST"
 * - "3:00 PM EST" -> "3:00 PM EST"
 */
export function formatTime12HourEST(timeStr?: string): string {
  if (!timeStr) return '';
  const trimmed = timeStr.trim();
  if (!trimmed) return '';

  // If already contains AM/PM and EST/EDT
  if (/\b(AM|PM)\b/i.test(trimmed) && /\b(EST|EDT)\b/i.test(trimmed)) {
    return trimmed;
  }

  // If already contains AM/PM without EST
  if (/\b(AM|PM)\b/i.test(trimmed)) {
    return `${trimmed} EST`;
  }

  // 24-hour time HH:mm or HH:mm:ss
  const match = trimmed.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (match) {
    let hours = parseInt(match[1], 10);
    const minutes = match[2];
    const period = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    if (hours === 0) hours = 12;
    return `${hours}:${minutes} ${period} EST`;
  }

  // ISO timestamp string
  if (trimmed.includes('T') || !isNaN(Date.parse(trimmed))) {
    try {
      const d = new Date(trimmed);
      if (!isNaN(d.getTime())) {
        const timePart = d.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
          timeZone: 'America/New_York',
        });
        return `${timePart} EST`;
      }
    } catch (e) {
      // fallback
    }
  }

  return `${trimmed} EST`;
}

/**
 * Converts a 12-hour or formatted time string into 24-hour HH:mm string for HTML <input type="time">
 */
export function to24HourTime(timeStr?: string): string {
  if (!timeStr) return '';
  const trimmed = timeStr.trim();

  // Match 24-hour "15:00" or "09:30"
  const match24 = trimmed.match(/^(\d{2}):(\d{2})$/);
  if (match24) return trimmed;

  // Match 12-hour "3:00 PM" or "03:00 PM EST"
  const match12 = trimmed.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (match12) {
    let hours = parseInt(match12[1], 10);
    const minutes = match12[2];
    const period = match12[3].toUpperCase();
    if (period === 'PM' && hours < 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    const hh = hours.toString().padStart(2, '0');
    return `${hh}:${minutes}`;
  }

  return trimmed;
}

/**
 * Formats date string and time string together into 12-hour Eastern Time format.
 * Example: ("2026-10-01", "15:00") -> "10/1/2026 3:00 PM EST"
 */
export function formatDateTime12HourEST(dateStr?: string, timeStr?: string): string {
  if (!dateStr && !timeStr) return '';
  const timeFormatted = formatTime12HourEST(timeStr);
  if (!dateStr) return timeFormatted;
  return `${dateStr} ${timeFormatted}`.trim();
}

/**
 * Safely parses a Date of Birth string (YYYY-MM-DD, MM/DD/YYYY, or ISO string)
 * into a local Date object preserving local calendar year, month, and day (preventing UTC timezone shifting).
 */
export function parseLocalDate(dobString?: string | null): Date | null {
  if (!dobString || typeof dobString !== 'string') return null;
  const clean = dobString.trim();
  if (!clean) return null;

  // Match YYYY-MM-DD or YYYY/MM/DD
  const ymdMatch = clean.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  if (ymdMatch) {
    const year = parseInt(ymdMatch[1], 10);
    const month = parseInt(ymdMatch[2], 10) - 1; // 0-indexed month
    const day = parseInt(ymdMatch[3], 10);
    const d = new Date(year, month, day);
    return isNaN(d.getTime()) ? null : d;
  }

  // Match MM/DD/YYYY
  const mdyMatch = clean.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
  if (mdyMatch) {
    const month = parseInt(mdyMatch[1], 10) - 1;
    const day = parseInt(mdyMatch[2], 10);
    const year = parseInt(mdyMatch[3], 10);
    const d = new Date(year, month, day);
    return isNaN(d.getTime()) ? null : d;
  }

  // Fallback for full ISO timestamps
  const d = new Date(clean);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Calculates exact age in years based on local calendar birthdate.
 */
export function calculateAge(dobString?: string | null, referenceDate: Date = new Date()): number {
  if (!dobString) return 0;
  const birthDate = parseLocalDate(dobString);
  if (!birthDate) return 0;

  let age = referenceDate.getFullYear() - birthDate.getFullYear();
  const m = referenceDate.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && referenceDate.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

/**
 * Formats a Date of Birth string to "MMM D, YYYY" (e.g., "Nov 2, 2008") in local time without UTC offset shifts.
 */
export function formatDateOfBirth(dobString?: string | null): string {
  if (!dobString) return '';
  const d = parseLocalDate(dobString);
  if (!d) return dobString || '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

